import type { PumpStatus } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  PumpAlgorithm,
  ControlIQMode,
} from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ExerciseFillIcon,
  SleepFillIcon,
} from "@tandemdiabetes/sugarmate-react-components"
import type {
  FC,
  SVGProps,
} from "react"
import { useMemo } from "react"

import styles from "./PumpStatusModalContent.module.scss"

import { usePolyglot } from "src/contexts"
import { DataRecency } from "src/types"

const polyglotPrefix = "pages.home.pump_status_modal.control_iq_mode."

interface Props {
  pumpStatus: PumpStatus;
  pumpStatusRecency: DataRecency;
}

/*
 * this renders a row showing the control IQ mode if relevant
 */
export function ControlIQModeRow(props: Props) {
  const {
    pumpStatus,
    pumpStatusRecency,
  } = props

  const {
    pump_algorithm: pumpAlgorithm,
    ciq_mode: controlIQMode,
  } = pumpStatus

  const polyglot = usePolyglot()

  const Icon: FC<SVGProps<SVGElement>> | null = useMemo(
    () => {
      if (pumpAlgorithm !== PumpAlgorithm.ControlIQ) {
        return null
      }

      if (controlIQMode === ControlIQMode.Exercising) {
        return ExerciseFillIcon
      }

      if (controlIQMode === ControlIQMode.Sleeping) {
        return SleepFillIcon
      }

      return null
    },
    [
      pumpAlgorithm,
      controlIQMode,
    ],
  )

  const modeString: string | null = useMemo(
    () => {
      if (pumpAlgorithm !== PumpAlgorithm.ControlIQ) {
        return null
      }

      if (controlIQMode === ControlIQMode.Exercising) {
        return polyglot.t(`${polyglotPrefix}exercising`)
      }

      if (controlIQMode === ControlIQMode.Sleeping) {
        return polyglot.t(`${polyglotPrefix}sleeping`)
      }

      return null
    },
    [
      pumpAlgorithm,
      controlIQMode,
      polyglot,
    ],
  )

  if (pumpStatusRecency === DataRecency.Stale) {
    return null
  }

  if (!Icon || !modeString) {
    return null
  }

  return (
    <div className={styles.sectionRowWithIndent}>
      <Icon className={styles.controlIQIcon} />
      <div className={styles.sectionTitle}>
        {modeString}
      </div>
    </div>
  )
}
